import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import api from '../../../utils/api'
import AdminLayout from '../components/AdminLayout';
import { TalentRequestsIndexResponse, TalentRequestListItem } from './types';

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 16px;
  color: #6b7280;
  a { color: #00a6c2; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const PageTitle = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #d97706;
  margin: 0;
`;

const Section = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 24px;
  margin-bottom: 24px;
`;

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 12px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  border: 1px solid #e5e7eb;
  background: #f9fafb;
`;

const Td = styled.td<{ $deleted?: boolean }>`
  padding: 10px 12px;
  font-size: 14px;
  color: ${({ $deleted }) => ($deleted ? '#dc2626' : '#374151')};
  border: 1px solid #e5e7eb;
`;

const TalentRequestLink = styled(Link)`
  color: #00a6c2;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const EmptyRow = styled.div`
  padding: 16px;
  color: #6b7280;
  background: #f9fafb;
  border-radius: 4px;
  text-align: center;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 48px;
  color: #6b7280;
`;

const formatDate = (dateStr?: string) => {
  if (!dateStr) return 'N/A';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
};

const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return 'Never updated';
  const date = new Date(dateStr);
  return `(${date.toLocaleString()})`;
};

function AdminPublicationTalentRequestsPage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<TalentRequestsIndexResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`/admin2/publications/${id}/talent_requests.json`);
      setData(res.data);
    } catch (err) {
      console.error('Failed to load talent requests', err);
      setError('Failed to load talent requests');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const pubDisplayName = data?.publication 
    ? `${data.publication.account?.name} - ${data.publication.name}` 
    : 'Publication';

  if (loading) {
    return (
      <AdminLayout 
        title="Talent Requests" 
        breadcrumb={[
          { label: 'Publications', href: '/admin2/publications' },
          { label: 'Loading...' }
        ]}
      >
        <LoadingState>Loading...</LoadingState>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout 
        title="Talent Requests" 
        breadcrumb={[
          { label: 'Publications', href: '/admin2/publications' },
          { label: 'Error' }
        ]}
      >
        <Section>
          <EmptyRow>{error}</EmptyRow>
        </Section>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout 
      title="Talent Requests" 
      breadcrumb={[
        { label: 'Publications', href: '/admin2/publications' },
        { label: pubDisplayName, href: `/admin2/publications/${id}/edit` },
        { label: 'Talent requests' }
      ]}
    >
      <Breadcrumb>
        <Link to="/admin2/publications">Publications</Link>
        <span>›</span>
        <Link to={`/admin2/publications/${id}/edit`}>{pubDisplayName}</Link>
        <span>›</span>
        <span>Talent requests</span>
      </Breadcrumb>

      <PageHeader>
        <PageTitle>Talent Requests</PageTitle>
      </PageHeader>

      <Section>
        {data?.talentRequests && data.talentRequests.length > 0 ? (
          <Table>
            <thead>
              <tr>
                <Th>ID</Th>
                <Th>Title</Th>
                <Th>Submitted</Th>
                <Th>Needed by</Th>
                <Th>Unfulfilled custom requests</Th>
                <Th>Deleted</Th>
                <Th>Eligible for ITR (last update date)</Th>
              </tr>
            </thead>
            <tbody>
              {data.talentRequests.map((tr: TalentRequestListItem) => (
                <tr key={tr.id}>
                  <Td>
                    <TalentRequestLink to={`/admin2/publications/${id}/talent_requests/${tr.id}`}>
                      {tr.id}
                    </TalentRequestLink>
                  </Td>
                  <Td>
                    <TalentRequestLink to={`/admin2/publications/${id}/talent_requests/${tr.id}`}>
                      {tr.title}
                    </TalentRequestLink>
                  </Td>
                  <Td>{formatDate(tr.submittedAt)}</Td>
                  <Td>{formatDate(tr.neededBy)}</Td>
                  <Td>{tr.unfulfilledCustomRequests}</Td>
                  <Td $deleted={!!tr.deletedAt}>
                    {tr.deletedAt ? formatDate(tr.deletedAt) : ''}
                  </Td>
                  <Td>
                    {tr.eligibleForItr ? 'true' : 'false'}
                    {tr.eligibleForItr && (
                      <span style={{ marginLeft: '8px', color: '#6b7280' }}>
                        {tr.lastRefreshedAt ? formatDateTime(tr.lastRefreshedAt) : '(Never updated)'}
                      </span>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <EmptyRow>No talent requests found</EmptyRow>
        )}
      </Section>
    </AdminLayout>
  );
}

export default AdminPublicationTalentRequestsPage;
